'use client';

/**
 * SidebarPanel Component (200px)
 *
 * Expandable panel that slides out from the right edge of the SidebarRail.
 * Shows items for the selected section with compact ClickUp-style items.
 *
 * @see PR #856 — Navigation & Layout Redesign
 */

import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import ArticleIcon from '@mui/icons-material/Article';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import BarChartIcon from '@mui/icons-material/BarChart';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BusinessIcon from '@mui/icons-material/Business';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import CalculateIcon from '@mui/icons-material/Calculate';
import ChecklistIcon from '@mui/icons-material/Checklist';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CodeIcon from '@mui/icons-material/Code';
import ContactsIcon from '@mui/icons-material/Contacts';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DescriptionIcon from '@mui/icons-material/Description';
import DevicesIcon from '@mui/icons-material/Devices';
import DomainIcon from '@mui/icons-material/Domain';
import DrawIcon from '@mui/icons-material/Draw';
import EmailIcon from '@mui/icons-material/Email';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import ExtensionIcon from '@mui/icons-material/Extension';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import GavelIcon from '@mui/icons-material/Gavel';
import GroupIcon from '@mui/icons-material/Group';
import GroupsIcon from '@mui/icons-material/Groups';
import HandshakeIcon from '@mui/icons-material/Handshake';
import HistoryIcon from '@mui/icons-material/History';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import HomeIcon from '@mui/icons-material/Home';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import HubIcon from '@mui/icons-material/Hub';
import InboxIcon from '@mui/icons-material/Inbox';
import InfoIcon from '@mui/icons-material/Info';
import KeyIcon from '@mui/icons-material/Key';
import LanguageIcon from '@mui/icons-material/Language';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import LinkIcon from '@mui/icons-material/Link';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LockIcon from '@mui/icons-material/Lock';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PageviewIcon from '@mui/icons-material/Pageview';
import PaidIcon from '@mui/icons-material/Paid';
import PaymentIcon from '@mui/icons-material/Payment';
import PeopleIcon from '@mui/icons-material/People';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import PhoneIcon from '@mui/icons-material/Phone';
import PieChartIcon from '@mui/icons-material/PieChart';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay';
import PsychologyIcon from '@mui/icons-material/Psychology';
import PublishIcon from '@mui/icons-material/Publish';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import RadarIcon from '@mui/icons-material/Radar';
import ReceiptIcon from '@mui/icons-material/Receipt';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SchemaIcon from '@mui/icons-material/Schema';
import SchoolIcon from '@mui/icons-material/School';
import ScienceIcon from '@mui/icons-material/Science';
import ScoreboardIcon from '@mui/icons-material/Scoreboard';
import SecurityIcon from '@mui/icons-material/Security';
import SendIcon from '@mui/icons-material/Send';
import SettingsIcon from '@mui/icons-material/Settings';
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent';
import ShieldIcon from '@mui/icons-material/Shield';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import StorageIcon from '@mui/icons-material/Storage';
import StorefrontIcon from '@mui/icons-material/Storefront';
import TableRowsIcon from '@mui/icons-material/TableRows';
import TimelineIcon from '@mui/icons-material/Timeline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TuneIcon from '@mui/icons-material/Tune';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import WebhookIcon from '@mui/icons-material/Webhook';
import WidgetsIcon from '@mui/icons-material/Widgets';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import {
  Badge,
  Box,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { NavItem, NavSection, VisibilityContext } from '@ops-matrix/shared-types';
import { detectEnvironment } from '@ops-matrix/shared-types';
import { GlassChip } from '../GlassChip/GlassChip';

/** Module-level constant — environment cannot change within a session */
const IS_PRODUCTION = detectEnvironment() === 'production';

/** Extended NavItem with optional section header (from NavTab.section) */
interface PanelItem extends NavItem {
  /** Section header to render above this item (first item in group) */
  sectionHeader?: string;
  /** String badge from NavTab (e.g. 'comingSoon', 'new', 'beta') */
  tabBadge?: string;
}

import { checkVisibility } from '@ops-matrix/shared-types';
import type React from 'react';
import { Fragment, useMemo } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { PANEL_WIDTH, RAIL_WIDTH } from '../../hooks/useSidebarRail';
import { iconSizes } from '../../theme/typography';
import { sortItemsByTranslatedLabel } from '../../utils/sortNavItems';
import { HEADER_HEIGHT } from '../AppNavBar/AppNavBar';

// ============================================================================
// Icon Mapping
// ============================================================================

const PANEL_ICON_MAP: Record<string, React.ReactElement> = {
  // Core navigation
  Home: <HomeIcon fontSize="small" />,
  Dashboard: <DashboardIcon fontSize="small" />,
  Inbox: <InboxIcon fontSize="small" />,
  Notifications: <NotificationsIcon fontSize="small" />,
  Settings: <SettingsIcon fontSize="small" />,
  SettingsInputComponent: <SettingsInputComponentIcon fontSize="small" />,
  // Signature / documents-to-sign / scorecard / pricing / dev / storage
  Draw: <DrawIcon fontSize="small" />,
  HistoryEdu: <HistoryEduIcon fontSize="small" />,
  Scoreboard: <ScoreboardIcon fontSize="small" />,
  LocalOffer: <LocalOfferIcon fontSize="small" />,
  Code: <CodeIcon fontSize="small" />,
  Storage: <StorageIcon fontSize="small" />,
  Bookmark: <BookmarkIcon fontSize="small" />,
  Checklist: <ChecklistIcon fontSize="small" />,
  // Documents & files
  Assignment: <AssignmentIcon fontSize="small" />,
  Description: <DescriptionIcon fontSize="small" />,
  Folder: <FolderIcon fontSize="small" />,
  FolderOpen: <FolderOpenIcon fontSize="small" />,
  FolderShared: <FolderSharedIcon fontSize="small" />,
  FileCopy: <FileCopyIcon fontSize="small" />,
  ContentCopy: <ContentCopyIcon fontSize="small" />,
  Article: <ArticleIcon fontSize="small" />,
  FormatQuote: <FormatQuoteIcon fontSize="small" />,
  // People & teams
  Contacts: <ContactsIcon fontSize="small" />,
  People: <PeopleIcon fontSize="small" />,
  Groups: <GroupsIcon fontSize="small" />,
  Group: <GroupIcon fontSize="small" />,
  PersonSearch: <PersonSearchIcon fontSize="small" />,
  HowToReg: <HowToRegIcon fontSize="small" />,
  // Organization
  Domain: <DomainIcon fontSize="small" />,
  Business: <BusinessIcon fontSize="small" />,
  BusinessCenter: <BusinessCenterIcon fontSize="small" />,
  AccountTree: <AccountTreeIcon fontSize="small" />,
  Schema: <SchemaIcon fontSize="small" />,
  Workspaces: <WorkspacesIcon fontSize="small" />,
  // Pipeline & workflow
  Timeline: <TimelineIcon fontSize="small" />,
  Leaderboard: <LeaderboardIcon fontSize="small" />,
  TableRows: <TableRowsIcon fontSize="small" />,
  PlaylistPlay: <PlaylistPlayIcon fontSize="small" />,
  PlaylistAddCheck: <PlaylistAddCheckIcon fontSize="small" />,
  History: <HistoryIcon fontSize="small" />,
  Equalizer: <EqualizerIcon fontSize="small" />,
  // Finance & billing
  CreditCard: <CreditCardIcon fontSize="small" />,
  Payment: <PaymentIcon fontSize="small" />,
  AccountBalance: <AccountBalanceIcon fontSize="small" />,
  AttachMoney: <AttachMoneyIcon fontSize="small" />,
  Paid: <PaidIcon fontSize="small" />,
  Receipt: <ReceiptIcon fontSize="small" />,
  // Partnerships & commerce
  Handshake: <HandshakeIcon fontSize="small" />,
  Storefront: <StorefrontIcon fontSize="small" />,
  Gavel: <GavelIcon fontSize="small" />,
  // Communication
  Email: <EmailIcon fontSize="small" />,
  Phone: <PhoneIcon fontSize="small" />,
  Send: <SendIcon fontSize="small" />,
  // AI & automation
  AutoAwesome: <AutoAwesomeIcon fontSize="small" />,
  AutoFixHigh: <AutoFixHighIcon fontSize="small" />,
  Psychology: <PsychologyIcon fontSize="small" />,
  SmartToy: <SmartToyIcon fontSize="small" />,
  Hub: <HubIcon fontSize="small" />,
  // Analytics & monitoring
  Analytics: <AnalyticsIcon fontSize="small" />,
  Assessment: <AssessmentIcon fontSize="small" />,
  BarChart: <BarChartIcon fontSize="small" />,
  ShowChart: <ShowChartIcon fontSize="small" />,
  PieChart: <PieChartIcon fontSize="small" />,
  QueryStats: <QueryStatsIcon fontSize="small" />,
  TrendingUp: <TrendingUpIcon fontSize="small" />,
  Radar: <RadarIcon fontSize="small" />,
  MonitorHeart: <MonitorHeartIcon fontSize="small" />,
  Tune: <TuneIcon fontSize="small" />,
  Calculate: <CalculateIcon fontSize="small" />,
  // Security & compliance
  Shield: <ShieldIcon fontSize="small" />,
  Security: <SecurityIcon fontSize="small" />,
  VerifiedUser: <VerifiedUserIcon fontSize="small" />,
  Key: <KeyIcon fontSize="small" />,
  Lock: <LockIcon fontSize="small" />,
  Fingerprint: <FingerprintIcon fontSize="small" />,
  AdminPanelSettings: <AdminPanelSettingsIcon fontSize="small" />,
  RocketLaunch: <RocketLaunchIcon fontSize="small" />,
  // Platform & development
  Widgets: <WidgetsIcon fontSize="small" />,
  Info: <InfoIcon fontSize="small" />,
  Science: <ScienceIcon fontSize="small" />,
  SportsEsports: <SportsEsportsIcon fontSize="small" />,
  PlayCircle: <PlayCircleIcon fontSize="small" />,
  Publish: <PublishIcon fontSize="small" />,
  Link: <LinkIcon fontSize="small" />,
  Language: <LanguageIcon fontSize="small" />,
  Devices: <DevicesIcon fontSize="small" />,
  School: <SchoolIcon fontSize="small" />,
  // Integrations
  Extension: <ExtensionIcon fontSize="small" />,
  Webhook: <WebhookIcon fontSize="small" />,
  // Watchlist / saved views — same icon used by the home Watchlist card.
  Pageview: <PageviewIcon fontSize="small" />,
};

function getPanelIcon(iconName: string): React.ReactElement {
  return PANEL_ICON_MAP[iconName] || <DashboardIcon fontSize="small" />;
}

// ============================================================================
// Types
// ============================================================================

export interface SidebarPanelProps {
  /** The section to display items for */
  section: NavSection | null;
  /** Whether the panel is open */
  open: boolean;
  /** Current pathname for active state */
  pathname: string;
  /** Navigation handler */
  onNavigate: (path: string) => void;
  /** Visibility context for filtering items */
  visibilityContext?: VisibilityContext;
  /** Dynamic badge counts */
  badgeCounts?: Record<string, number>;
  /** Handler to collapse the panel */
  onCollapse?: () => void;
  /** Whether both rail and panel are fully hidden (Asana-style full collapse) */
  hidden?: boolean;
  /**
   * Pin state for hoverExpand mode. When defined, the collapse chevron
   * is swapped for a pin/unpin icon and the button's aria-label reflects
   * the pin semantics (clicked → latch panel open across hovers). Leave
   * `undefined` to keep the existing collapse behaviour.
   */
  pinned?: boolean;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Among a list of sibling items, find the one whose path best matches the
 * current pathname.  "Best" = longest matching prefix.  This prevents parent
 * paths (e.g. `/partners`) from highlighting when a child path
 * (e.g. `/partners/applications`) also matches.
 */
function findActiveItemId(items: NavItem[], pathname: string): string | null {
  let bestId: string | null = null;
  let bestLen = -1;

  for (const item of items) {
    if (pathname === item.path || pathname.startsWith(`${item.path}/`)) {
      if (item.path.length > bestLen) {
        bestId = item.id;
        bestLen = item.path.length;
      }
    }
    if (item.tabs) {
      for (const tab of item.tabs) {
        if (pathname === tab.path || pathname.startsWith(`${tab.path}/`)) {
          if (tab.path.length > bestLen) {
            bestId = item.id;
            bestLen = tab.path.length;
          }
        }
      }
    }
  }

  return bestId;
}

// ============================================================================
// Component
// ============================================================================

export const SidebarPanel: React.FC<SidebarPanelProps> = ({
  section,
  open,
  pathname,
  onNavigate,
  visibilityContext = {},
  badgeCounts = {},
  onCollapse,
  hidden = false,
  pinned,
}) => {
  const theme = useTheme();
  const { t } = useI18n();
  const isDark = theme.palette.mode === 'dark';
  // Filter items by visibility.
  // When a section has a single item with multiple tabs, promote the tabs
  // as panel items so the 200px panel shows the sub-navigation (e.g., Loans →
  // Applications, Borrowers, Cases …) instead of just the parent item.
  const visibleItems = useMemo((): PanelItem[] => {
    if (!section) return [];
    const filtered = section.items.filter(
      (item) => !item.visibility || checkVisibility(item.visibility, visibilityContext),
    );

    // Single item with tabs → show tabs as panel items
    if (filtered.length === 1 && filtered[0].tabs && filtered[0].tabs.length > 1) {
      const parent = filtered[0];
      const tabs = parent.tabs!;
      const promoted = tabs
        .filter((tab) => !tab.visibility || checkVisibility(tab.visibility, visibilityContext))
        .map(
          (tab): PanelItem => ({
            id: tab.id,
            label: tab.label,
            path: tab.path,
            icon: tab.icon || parent.icon,
            disabled: tab.disabled,
            sectionHeader: tab.section,
            tabBadge: tab.badge,
          }),
        );
      return sortItemsByTranslatedLabel(promoted, t);
    }

    return sortItemsByTranslatedLabel(filtered, t);
  }, [section, visibilityContext, t]);

  // Compute best-match active item (longest path wins among siblings)
  const activeItemId = useMemo(() => findActiveItemId(visibleItems, pathname), [visibleItems, pathname]);

  const sectionLabel = section?.label ? t(section.label) || section.label : '';

  // When the panel is closed (hover-collapsed or fully hidden), the nav
  // subtree must be removed from the accessibility tree AND the keyboard /
  // pointer-event surface. Otherwise screen readers announce phantom links
  // ("Preferences", "Devices", …) inside an invisible panel, and Playwright
  // can still resolve `nav[aria-label="… Navigation"] >> getByText('Preferences')`
  // → walk the off-screen DOM → click at coordinates that land on the
  // SidebarRail's `.MuiDrawer-root` because the panel is `pointer-events: none`
  // but still occupies layout coordinates. `inert` covers both surfaces in one
  // attribute (React 19 supports it natively).
  const isInert = hidden || !open;

  return (
    <Box
      component="nav"
      aria-label={sectionLabel ? `${sectionLabel} Navigation` : 'Sidebar Navigation'}
      aria-hidden={isInert || undefined}
      inert={isInert || undefined}
      sx={{
        position: 'fixed',
        top: HEADER_HEIGHT,
        left: RAIL_WIDTH,
        width: PANEL_WIDTH,
        height: `calc(100vh - ${HEADER_HEIGHT}px)`,
        // Unified glass background (matches AppNavBar and SidebarRail)
        bgcolor: isDark ? 'rgba(18, 18, 20, 0.92)' : 'rgba(245, 245, 248, 0.95)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        borderRight: `1px solid ${theme.palette.divider}`,
        borderTop: `1px solid ${theme.palette.divider}`,
        borderLeft: `1px solid ${theme.palette.divider}`,
        // Asana-style curved top-left corner — uses theme shape so it matches
        // the rest of the surface radii (was previously hardcoded 8px).
        borderRadius: `${theme.shape.borderRadius}px 0 0 0`,
        // Slide/fade transitions — full hide when `hidden`, panel collapse when `!open`
        transform: hidden
          ? `translateX(-${RAIL_WIDTH + PANEL_WIDTH}px)`
          : open
            ? 'translateX(0)'
            : `translateX(-${PANEL_WIDTH}px)`,
        opacity: hidden ? 0 : open ? 1 : 0,
        // `visibility: hidden` strips the subtree from the hit-test tree and
        // accessibility tree. Combined with `inert`, it prevents Playwright's
        // `getByText` from resolving anywhere inside a closed panel — without
        // this, the click would land at a screen position covered by the
        // SidebarRail (`MuiDrawer-root`) and Playwright would refuse with
        // "intercepted by MuiDrawer pointer events". The visibility flip is
        // delayed on close (0.25s) so the slide-out animation can play; on
        // open it's instant so the panel becomes interactive as it slides in.
        visibility: isInert ? 'hidden' : 'visible',
        pointerEvents: isInert ? 'none' : 'auto',
        transition: `transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1), visibility 0s linear ${
          isInert ? '0.25s' : '0s'
        }`,
        // Sit BEHIND the SidebarRail so the slide-in animation tucks
        // under the rail instead of painting over it. The panel
        // translates from `translateX(-PANEL_WIDTH)` while opening, which
        // crosses the rail's horizontal range — at equal z-index the
        // panel briefly clipped the rail icons. -1 keeps the rail's
        // identity column always visible.
        zIndex: theme.zIndex.drawer - 1,
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      {/* Section Header with collapse button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: sectionLabel ? 'space-between' : 'flex-end',
          px: 2,
          pt: 1.5,
          pb: 0.5,
          minHeight: 32,
        }}
      >
        {sectionLabel && (
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              letterSpacing: 0.3,
              color: 'text.primary',
              fontSize: '0.85rem',
            }}
          >
            {sectionLabel}
          </Typography>
        )}
        {onCollapse && (
          <IconButton
            onClick={onCollapse}
            size="small"
            // In hoverExpand mode (`pinned` is defined) the toggle is a
            // pin/unpin action — fill the icon when pinned, outline when
            // floating. Aria-label reflects the next action so screen
            // readers announce "Pin sidebar open" / "Unpin sidebar".
            // In legacy collapse mode (`pinned === undefined`) the button
            // is a chevron-collapse, so fall back to the existing
            // `a11y.toggleSidebar` label so the icon-only button never
            // ships without an accessible name.
            aria-label={
              pinned === undefined
                ? t('a11y.toggleSidebar', { defaultValue: 'Toggle sidebar' })
                : pinned
                  ? t('navigation.unpinSidebar', { defaultValue: 'Unpin sidebar' })
                  : t('navigation.pinSidebar', { defaultValue: 'Pin sidebar open' })
            }
            sx={{
              // Pinned state stays muted by default so the icon doesn't pull
              // focus from real navigation; the primary tint only appears on
              // hover/focus to confirm the action is the unpin toggle.
              color: 'text.secondary',
              p: 1,
              minWidth: 40,
              minHeight: 40,
              '&:hover, &:focus-visible': {
                bgcolor: 'action.hover',
                color: pinned ? 'primary.main' : 'text.secondary',
              },
            }}
          >
            {pinned === undefined ? (
              <ChevronLeftIcon sx={{ fontSize: iconSizes.md }} />
            ) : pinned ? (
              <PushPinIcon sx={{ fontSize: iconSizes.md }} />
            ) : (
              <PushPinOutlinedIcon sx={{ fontSize: iconSizes.md }} />
            )}
          </IconButton>
        )}
      </Box>
      {/* Items */}
      <List sx={{ px: 0.75, py: 0.5 }} dense>
        {visibleItems.map((item, index) => {
          const active = item.id === activeItemId;
          const badgeCount = badgeCounts[item.id] || (typeof item.badge?.count === 'number' ? item.badge.count : 0);
          const label = t(item.label) || item.label;
          // Render the section header ONLY at group boundaries: the first
          // item with a sectionHeader, or any item whose sectionHeader
          // differs from the previous visible item's. Without this gate,
          // annotating every tab with a `section` (so sortItemsByTranslatedLabel
          // can group them correctly) would re-emit the subheader per row —
          // the user would see "ASTOR AI" three times stacked above Agents,
          // Analytics, Autonomy instead of once at the top of the group.
          const previousSection = index > 0 ? visibleItems[index - 1].sectionHeader : undefined;
          const isGroupBoundary = Boolean(item.sectionHeader) && item.sectionHeader !== previousSection;
          const sectionLabel = isGroupBoundary ? t(item.sectionHeader as string) || item.sectionHeader : '';
          const isComingSoon = item.tabBadge === 'comingSoon';
          const isDisabledByComingSoon = isComingSoon && IS_PRODUCTION;

          return (
            <Fragment key={item.id}>
              {sectionLabel && (
                <ListSubheader
                  disableSticky
                  sx={{
                    lineHeight: '24px',
                    fontSize: '0.6875rem',
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'text.disabled',
                    bgcolor: 'transparent',
                    px: 1.5,
                    pt: index > 0 ? 1.25 : 0,
                    pb: 0.25,
                  }}
                >
                  {sectionLabel}
                </ListSubheader>
              )}
              <ListItemButton
                onClick={() => !isDisabledByComingSoon && onNavigate(item.path)}
                selected={active && !isDisabledByComingSoon}
                disabled={item.disabled || isDisabledByComingSoon}
                sx={{
                  borderRadius: 1,
                  py: 0.75,
                  px: 1.5,
                  mb: 0.25,
                  minHeight: 44,
                  // Mint left-edge marker for the active row — invisible by
                  // default so layout doesn't jump when selection changes.
                  borderLeft: '2px solid transparent',
                  '&.Mui-selected': {
                    // Mint-tinted active fill (F.mintDim ≈ alpha(mint, 0.14))
                    // replaces the legacy indigo `rgba(99,102,241,…)` which
                    // reads violet on the navy panel — see PR #856.
                    bgcolor: alpha(theme.palette.primary.main, isDark ? 0.14 : 0.1),
                    color: 'primary.main',
                    borderLeftColor: 'primary.main',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, isDark ? 0.2 : 0.14),
                    },
                  },
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                  ...(isDisabledByComingSoon && {
                    opacity: 0.55,
                    cursor: 'default',
                  }),
                  transition: 'all 0.1s ease',
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 28,
                    color: active && !isDisabledByComingSoon ? 'primary.main' : 'text.secondary',
                  }}
                >
                  {getPanelIcon(item.icon)}
                </ListItemIcon>
                <ListItemText
                  primary={label}
                  slotProps={{
                    primary: {
                      variant: 'body2',
                      noWrap: true,
                      // Color is applied via `sx` (not Typography's `color` prop) because
                      // MUI v9's Typography.color accepts only `textPrimary` / `textSecondary`
                      // literals — palette paths like `text.primary` fall through to raw
                      // CSS and render as invalid/inherit, producing nearly-invisible text
                      // against the panel's translucent glass background in light mode.
                      sx: {
                        fontSize: '0.8rem',
                        fontWeight: active && !isDisabledByComingSoon ? 600 : 400,
                        color: active && !isDisabledByComingSoon ? 'primary.main' : 'text.primary',
                      },
                    },
                  }}
                />
                {isComingSoon && (
                  <GlassChip
                    label={t('common.soon')}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      // Mint-tinted chip — matches the new mint active state above
                      // (was legacy indigo `rgba(99,102,241,…)`).
                      bgcolor: alpha(theme.palette.primary.main, isDark ? 0.15 : 0.1),
                      color: 'primary.main',
                      '& .MuiChip-label': { px: 0.75 },
                    }}
                  />
                )}
                {badgeCount > 0 && (
                  <Badge
                    badgeContent={badgeCount}
                    color={item.badge?.color || 'primary'}
                    max={item.badge?.max || 99}
                    sx={{ ml: 1 }}
                  />
                )}
              </ListItemButton>
            </Fragment>
          );
        })}
      </List>
    </Box>
  );
};

export default SidebarPanel;
